import React, { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ru } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import './DateRangeSelector.css'; // Импортируем пользовательские стили
import { getDaysText } from '../../pages/EquipmentPage';

// Регистрируем русскую локализацию
registerLocale('ru', ru);

interface DateRangeSelectorProps {
    onDatesChange: (startDate: Date | null, endDate: Date | null) => void;
    className?: string;
    highlightAnimation?: boolean;
    forceOpen?: boolean; // Флаг для принудительного открытия календаря
    disabledDates?: Date[]; // Массив недоступных дат
    focusedEquipmentName?: string; // Название оборудования, для которого открыт календарь
    initialStartDate?: Date; // Начальная дата начала периода
    initialEndDate?: Date; // Начальная дата конца периода
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
    onDatesChange,
    className = '',
    highlightAnimation = false,
    forceOpen = false,
    disabledDates = [],
    focusedEquipmentName,
    initialStartDate = null,
    initialEndDate = null
}) => {
    const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
    const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
    const [isOpen, setIsOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    // Добавляем state для фиксированного месяца начала
    const [fixedStartMonth, setFixedStartMonth] = useState<Date | null>(new Date());
    const containerRef = useRef<HTMLDivElement>(null);
    const calendarRef = useRef<HTMLDivElement>(null);

    // Определяем, является ли устройство мобильным
    useEffect(() => {
        const checkIfMobile = () => {
            const mediaQuery = window.matchMedia('(max-width: 768px)');
            setIsMobile(mediaQuery.matches);
        };

        // Проверяем при первой загрузке
        checkIfMobile();

        // Добавляем listener для отслеживания изменения размера экрана
        const mediaQuery = window.matchMedia('(max-width: 768px)');
        mediaQuery.addEventListener('change', checkIfMobile);

        // Очистка listener при unmount
        return () => {
            mediaQuery.removeEventListener('change', checkIfMobile);
        };
    }, []);

    // Обновляем состояние, если изменились начальные даты
    useEffect(() => {
        console.log('DateRangeSelector: обновление начальных дат', { initialStartDate, initialEndDate });

        if (initialStartDate !== null && initialStartDate instanceof Date) {
            setStartDate(initialStartDate);
            console.log('DateRangeSelector: установлена начальная дата', initialStartDate);
        }

        if (initialEndDate !== null && initialEndDate instanceof Date) {
            setEndDate(initialEndDate);
            console.log('DateRangeSelector: установлена конечная дата', initialEndDate);
        }

        // Если обе даты установлены, вызываем onDatesChange для синхронизации с родительским компонентом
        if (initialStartDate && initialEndDate) {
            // Не вызываем onDatesChange здесь, чтобы избежать бесконечного цикла обновлений
            // onDatesChange(initialStartDate, initialEndDate);
        }
    }, [initialStartDate, initialEndDate]);

    // Отслеживаем изменение пропса forceOpen
    useEffect(() => {
        if (forceOpen) {
            setIsOpen(true);
        }
    }, [forceOpen]);

    // При первом открытии календаря устанавливаем фиксированный месяц
    useEffect(() => {
        if (isOpen && !fixedStartMonth) {
            setFixedStartMonth(new Date());
        }
    }, [isOpen]);

    const handleDateChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        console.log('DateRangeSelector handleDateChange:', { start, end });

        setStartDate(start);
        setEndDate(end);

        // Если выбрана только начальная дата, не вызываем onDatesChange,
        // это будет сделано при нажатии кнопки "Применить"
        if (start && end) {
            onDatesChange(start, end);
        }
    };

    const handleWidgetClick = () => {
        setIsOpen(!isOpen);

        // При открытии календаря устанавливаем фиксированный месяц на текущую дату
        if (!isOpen) {
            setFixedStartMonth(new Date());
        }
    };

    // Функция для применения выбранной даты
    const applySelectedDate = () => {
        console.log('DateRangeSelector applySelectedDate:', { startDate, endDate });

        // Если выбрана только начальная дата, используем её и как конечную
        if (startDate && !endDate) {
            setEndDate(startDate);
            console.log('DateRangeSelector: автоматически установлена конечная дата равной начальной');
            onDatesChange(startDate, startDate);
        } else if (startDate && endDate) {
            // Если обе даты выбраны, вызываем onDatesChange для синхронизации с родительским компонентом
            console.log('DateRangeSelector: применяем обе выбранные даты', { startDate, endDate });
            onDatesChange(startDate, endDate);
        }

        setIsOpen(false);
    };

    // Функция для сброса выбранных дат
    const resetSelectedDates = () => {
        setStartDate(null);
        setEndDate(null);
        onDatesChange(null, null);
        setIsOpen(false);
    };

    // Функция для определения недоступности даты
    const isDateDisabled = (date: Date): boolean => {
        if (!disabledDates || disabledDates.length === 0) return false;

        return disabledDates.some(disabledDate =>
            disabledDate.getFullYear() === date.getFullYear() &&
            disabledDate.getMonth() === date.getMonth() &&
            disabledDate.getDate() === date.getDate()
        );
    };

    // Закрытие календаря при клике вне компонента, если не установлен forceOpen
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (isOpen &&
                !forceOpen && // Не закрываем календарь, если forceOpen = true
                calendarRef.current &&
                !calendarRef.current.contains(event.target as Node) &&
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, forceOpen]);

    return (
        <div className={`bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300 relative 
                        ${highlightAnimation || forceOpen ? 'date-selector-highlight' : ''} ${className}`}
            ref={containerRef}
            style={{ height: '45px' }}
        >
            <div className="flex items-center justify-between cursor-pointer p-3 group h-full" onClick={handleWidgetClick}>
                <div className="flex-1 flex items-center">
                    <div className="text-gray-600 font-medium text-sm mr-2 group-hover:text-primary-600 transition-colors duration-200">
                        <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                        Даты:
                    </div>

                    <div className="flex-1">
                        {startDate && endDate ? (
                            <span className="text-yellow-500 font-medium text-sm">
                                {format(startDate, 'dd.MM.yyyy')}
                                {startDate.getTime() !== endDate.getTime() &&
                                    <>
                                        <span className="mx-1 text-yellow-500">→</span>
                                        {format(endDate, 'dd.MM.yyyy')}
                                    </>
                                }
                                <span className="ml-1 text-gray-500 font-normal">
                                    ({Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1 || 1} {getDaysText(Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1 || 1)})
                                </span>
                            </span>
                        ) : (
                            <span className="text-yellow-500 font-medium text-sm group-hover:text-yellow-600 transition-colors duration-200">
                                {focusedEquipmentName ? `Выберите даты для ${focusedEquipmentName}` : 'Выберите даты аренды'}
                            </span>
                        )}
                    </div>
                </div>

                <button
                    className={`flex items-center justify-center h-8 w-8 rounded-full transition-all duration-300
                               ${startDate && endDate
                            ? 'bg-primary text-white shadow-md'
                            : 'bg-gray-100 text-gray-400 group-hover:bg-primary-50 group-hover:text-primary-500'}`}
                    type="button"
                >
                    <svg
                        className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"></path>
                    </svg>
                </button>
            </div>

            {isOpen && (
                <div
                    className={`absolute left-0 right-0 top-full mt-1 z-[1000] bg-white rounded-xl shadow-2xl calendar-dropdown animate-slidein p-0 ${!isMobile ? 'calendar-dropdown-desktop' : ''}`}
                    ref={calendarRef}
                >
                    {focusedEquipmentName && (
                        <div className="bg-primary-50 text-primary-700 px-4 py-2 text-sm font-medium rounded-t-xl">
                            Выберите даты аренды для: {focusedEquipmentName}
                        </div>
                    )}

                    <div className="custom-datepicker-wrapper p-0 m-0 w-full">
                        <div className="custom-datepicker p-0 m-0 w-full">
                            <DatePicker
                                key={`datepicker-${startDate?.getTime()}-${endDate?.getTime()}`}
                                selected={startDate}
                                onChange={(dates) => {
                                    console.log('DatePicker onChange:', dates);
                                    if (Array.isArray(dates)) {
                                        handleDateChange([dates[0], dates[1]]);
                                    }
                                }}
                                startDate={startDate}
                                endDate={endDate}
                                selectsRange
                                inline
                                locale="ru"
                                monthsShown={isMobile ? 1 : 2}
                                minDate={new Date()}
                                dateFormat="dd.MM.yyyy"
                                className="w-full p-0 m-0"
                                calendarClassName="compact-calendar"
                                showPopperArrow={false}
                                filterDate={date => !isDateDisabled(date)}
                                preventOpenOnFocus={true}
                                shouldCloseOnSelect={false}
                                // Фиксируем открытый месяц календаря
                                openToDate={fixedStartMonth || undefined}
                            />
                        </div>
                    </div>
                    <div className="flex flex-row justify-between items-center gap-2 mt-1 pt-1 border-t p-2">
                        <div className="text-xs text-gray-600">
                            {startDate && endDate ? (
                                <span>
                                    Период: <span className="font-medium text-primary-700">{format(startDate, 'dd.MM.yyyy')}</span>
                                    {startDate.getTime() !== endDate.getTime() && (
                                        <>
                                            <span className="mx-1 text-gray-400">→</span>
                                            <span className="font-medium text-primary-700">{format(endDate, 'dd.MM.yyyy')}</span>
                                        </>
                                    )}
                                </span>
                            ) : startDate && !endDate ? (
                                <span>
                                    Выбрана дата: <span className="font-medium text-primary-700">{format(startDate, 'dd.MM.yyyy')}</span>
                                </span>
                            ) : (
                                <span>Выберите даты бронирования</span>
                            )}
                        </div>
                        <div className="flex gap-1">
                            {(startDate || endDate) && (
                                <button
                                    className="px-2 py-1 rounded-lg text-sm transition-all duration-300 bg-gray-200 text-gray-600 hover:bg-gray-300"
                                    onClick={resetSelectedDates}
                                    type="button"
                                >
                                    Сбросить
                                </button>
                            )}
                            <button
                                className={`px-2 py-1 rounded-lg text-sm transition-all duration-300
                                      ${startDate
                                        ? 'bg-primary text-white hover:bg-primary-600'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                                onClick={applySelectedDate}
                                disabled={!startDate}
                                type="button"
                            >
                                Применить
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateRangeSelector; 